import { authApi, backendApi } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { mapTokens } from './auth.mappers';
import type {
  AuthTokens,
  AuthTokensDto,
  AuthUser,
  LoginPayload,
  RegisterPayload,
  RequestPasswordResetPayload,
  ResetPasswordWithOtpPayload,
  SocialProvider,
  VerifyOtpPayload,
} from '../types/auth.types';

const exchangeEndpoint: Record<SocialProvider, string> = {
  google: endpoints.backend.exchangeGoogleToken,
  facebook: endpoints.backend.exchangeFacebookToken,
  apple: endpoints.backend.exchangeAppleToken,
};

/**
 * Thin, typed wrappers over the Gral auth endpoints. No React here — these are
 * pure async functions consumed by the auth hooks (TanStack mutations/queries).
 */
export const authApiService = {
  async login(payload: LoginPayload): Promise<AuthTokens> {
    // `scope: 'azure'` makes the auth server co-issue an Azure EasyAuth token
    // (x_zumo_auth) for MEDIS /v2. Must match the server's ZUMO_TRIGGER_SCOPE.
    const { data } = await backendApi.post<AuthTokensDto>(endpoints.backend.login, {
      ...payload,
      scope: 'azure',
    });
    return mapTokens(data);
  },

  /**
   * Create the account directly on the auth server (public endpoint, no Bearer).
   * Romanian field names + snake_case confirm, as the server validates them.
   * 422 carries per-field `errors`; 503 means the MEDIS account check failed.
   */
  async register(payload: RegisterPayload): Promise<{ id?: number; email?: string; message?: string }> {
    const { data } = await authApi.post(endpoints.auth.register, {
      nume: payload.nume,
      prenume: payload.prenume,
      email: payload.email,
      phone: payload.phone,
      password: payload.password,
      confirm_password: payload.confirmPassword,
    });
    return data;
  },

  /**
   * Confirm the emailed verification code for the signed-in account. Bearer
   * endpoint on the auth server; provider "main" = the email/password account,
   * the same choice the mobile app makes.
   */
  async verifyOtp(payload: VerifyOtpPayload): Promise<{ message?: string }> {
    const { data } = await authApi.post(endpoints.auth.verifyOtp, {
      email: payload.email,
      otp: payload.otp,
      provider: 'main',
    });
    return data;
  },

  /** Mail a fresh verification code to the signed-in account (Bearer endpoint). */
  async resendOtp(email: string): Promise<{ message?: string }> {
    const { data } = await authApi.post(endpoints.auth.resendOtp, {
      email,
      provider: 'main',
    });
    return data;
  },

  async requestPasswordReset(
    payload: RequestPasswordResetPayload,
  ): Promise<{ message: string }> {
    const { data } = await backendApi.post(endpoints.backend.requestPasswordReset, payload);
    return data;
  },

  async resetPasswordWithOtp(
    payload: ResetPasswordWithOtpPayload,
  ): Promise<{ message: string }> {
    const { data } = await backendApi.post(endpoints.backend.resetPasswordWithOtp, payload);
    return data;
  },

  /** Exchange a provider-issued token (id_token / access_token) for Gral tokens. */
  async exchangeSocialToken(
    provider: SocialProvider,
    providerToken: string,
  ): Promise<AuthTokens> {
    const { data } = await backendApi.post<AuthTokensDto>(exchangeEndpoint[provider], {
      token: providerToken,
      scope: 'azure', // co-issue Azure EasyAuth token for MEDIS /v2
    });
    return mapTokens(data);
  },

  async logout(): Promise<void> {
    await backendApi.post(endpoints.backend.logout);
  },

  /** Fetch the authenticated user from the auth server. */
  async me(): Promise<AuthUser> {
    const { data } = await authApi.get<AuthUser>(endpoints.auth.me);
    return data;
  },

  /**
   * Begin a web "attach social account" flow. The auth server mints a short-lived
   * ticket from our Bearer (so no token ends up in a URL) and returns the URL to
   * redirect the browser to; the provider round-trip + attach happen server-side.
   */
  async attachStart(provider: SocialProvider): Promise<{ redirect_url: string }> {
    const { data } = await authApi.post<{ redirect_url: string }>(endpoints.auth.attachStart, {
      provider,
    });
    return data;
  },

  /** Detach a previously attached social account (direct, Bearer-authenticated). */
  async detachSocialAccount(provider: SocialProvider | string, email: string): Promise<void> {
    await authApi.post(endpoints.auth.detachSocialAccount, { provider, email });
  },
};
